import * as T from 'three';
import type { SceneKit } from '@london-life/game/scene/kit';

// London's vehicles and people at their real size for the ride scene (a metre is a metre here; the map's
// vehicles are drawn smaller to sit among its blocks). Every vehicle faces +z, stands on the ground at its
// origin, and has its driver on the right, as in Britain. Lamps are lit boxes, so they glow at night.

const wheels = (kit: SceneKit, parent: T.Object3D, spots: [number, number][], r: number) => {
  for (const [x, z] of spots) {
    const wheel = kit.cylinder(r, 0.28, '#1c1f24', x, r, z, parent, r, 14);
    wheel.rotation.z = Math.PI / 2;
    const hub = kit.cylinder(r * 0.45, 0.3, '#9aa3ab', x, r, z, parent, r * 0.45, 10);
    hub.rotation.z = Math.PI / 2;
  }
};
const lamps = (kit: SceneKit, parent: T.Object3D, width: number, length: number, y: number) => {
  for (const side of [-1, 1]) {
    kit.lit(0.28, 0.2, 0.06, '#fff4d0', side * (width / 2 - 0.3), y, length / 2 + 0.02, parent, 1.6);
    kit.lit(0.24, 0.22, 0.06, '#e0302a', side * (width / 2 - 0.25), y + 0.05, -length / 2 - 0.02, parent, 1.2);
  }
};
// someone sitting (a driver, a passenger): just enough to see a head and shoulders through the glass
export function seated(
  kit: SceneKit,
  parent: T.Object3D,
  x: number,
  y: number,
  z: number,
  clothes: string,
  skin: string,
) {
  kit.box(0.46, 0.55, 0.3, clothes, x, y + 0.28, z, parent);
  kit.sphere(0.13, skin, x, y + 0.72, z, parent);
}

export function blackCab(kit: SceneKit) {
  const group = new T.Group();
  const paint = '#16191d';
  kit.box(1.78, 0.78, 4.5, paint, 0, 0.72, 0, group);
  kit.box(1.62, 0.72, 2.7, paint, 0, 1.46, -0.25, group);
  // the glass round the cabin (hidden from the seat, so the street shows through), the grille, the plates
  const glass = [
    ...[-1, 1].map((side) => kit.box(0.04, 0.46, 2.3, '#33414d', side * 0.82, 1.5, -0.25, group)),
    kit.box(1.5, 0.5, 0.04, '#3a4a57', 0, 1.5, 1.11, group),
  ];
  kit.box(1.5, 0.45, 0.04, '#33414d', 0, 1.5, -1.61, group);
  kit.box(0.9, 0.36, 0.06, '#b9c0c6', 0, 0.78, 2.26, group);
  kit.box(0.52, 0.12, 0.03, '#f4f4f4', 0, 0.55, 2.27, group);
  kit.box(0.52, 0.12, 0.03, '#f2c230', 0, 0.62, -2.27, group);
  glass.push(kit.lit(0.52, 0.16, 0.24, '#ffcc33', 0, 1.9, 0.6, group, 1.4));
  lamps(kit, group, 1.78, 4.5, 0.85);
  wheels(
    kit,
    group,
    [
      [-0.8, 1.45],
      [0.8, 1.45],
      [-0.8, -1.45],
      [0.8, -1.45],
    ],
    0.36,
  );
  // the driver sits on the right
  seated(kit, group, -0.38, 0.85, 0.55, '#2d3a4a', '#8d5a3c');
  return { group, length: 4.5, eye: new T.Vector3(0.35, 1.38, -0.75), glass };
}

// the player's own car (cars.ts) in its paint: a saloon with the glass round the cabin and the plates; the
// player at the wheel on the right, or in the back when a driver has the wheel
export function ownCar(kit: SceneKit, paint: string, driven: boolean) {
  const group = new T.Group();
  kit.box(1.8, 0.7, 4.4, paint, 0, 0.68, 0, group);
  kit.box(1.62, 0.64, 2.4, paint, 0, 1.32, -0.3, group);
  const glass = [
    ...[-1, 1].map((side) => kit.box(0.04, 0.44, 2.1, '#33414d', side * 0.82, 1.34, -0.3, group)),
    kit.box(1.48, 0.48, 0.04, '#3a4a57', 0, 1.34, 0.91, group),
  ];
  kit.box(1.48, 0.42, 0.04, '#33414d', 0, 1.34, -1.51, group);
  kit.box(0.9, 0.3, 0.06, '#2b2f36', 0, 0.72, 2.21, group);
  kit.box(0.52, 0.12, 0.03, '#f4f4f4', 0, 0.5, 2.22, group);
  kit.box(0.52, 0.12, 0.03, '#f2c230', 0, 0.56, -2.22, group);
  lamps(kit, group, 1.8, 4.4, 0.8);
  wheels(
    kit,
    group,
    [
      [-0.82, 1.4],
      [0.82, 1.4],
      [-0.82, -1.4],
      [0.82, -1.4],
    ],
    0.34,
  );
  // someone at the wheel on the right (the player, or their driver); the player's eye is at the wheel or in
  // the back seat
  seated(kit, group, -0.38, 0.8, 0.5, driven ? '#2d3a4a' : '#1c1f24', '#8d5a3c');
  const eye = driven ? new T.Vector3(-0.38, 1.3, 0.2) : new T.Vector3(0.38, 1.3, -0.75);
  return { group, length: 4.4, eye, glass };
}
// the player's scooter: two wheels, the footboard, the seat and the bars, the rider on it
export function scooter(kit: SceneKit, paint: string) {
  const group = new T.Group();
  kit.box(0.36, 0.22, 1.1, paint, 0, 0.42, -0.1, group);
  kit.box(0.34, 0.08, 0.5, '#1c1f24', 0, 0.3, 0.15, group);
  kit.box(0.38, 0.5, 0.3, paint, 0, 0.6, 0.62, group);
  kit.box(0.56, 0.05, 0.05, '#3d4650', 0, 0.98, 0.62, group);
  kit.box(0.4, 0.1, 0.5, '#2b2f36', 0, 0.6, -0.3, group);
  kit.lit(0.14, 0.14, 0.04, '#fff4d0', 0, 0.82, 0.78, group, 1.4);
  kit.lit(0.14, 0.08, 0.04, '#e0302a', 0, 0.55, -0.68, group, 1.2);
  for (const z of [0.62, -0.5]) {
    const wheel = kit.cylinder(0.2, 0.1, '#1c1f24', 0, 0.2, z, group, 0.2, 14);
    wheel.rotation.z = Math.PI / 2;
  }
  seated(kit, group, 0, 0.62, -0.25, '#2d3a4a', '#8d5a3c');
  return { group, length: 1.6, eye: new T.Vector3(0, 1.42, -0.2), glass: [] as T.Object3D[] };
}

export function doubleDecker(kit: SceneKit, destination: string) {
  const group = new T.Group();
  const red = '#c4232b';
  kit.box(2.5, 4.2, 10.4, red, 0, 2.4, 0, group);
  kit.box(2.52, 0.14, 10.42, '#8e1a20', 0, 4.48, 0, group);
  // the window bands, upstairs and down, and the big front windows
  const glass: T.Object3D[] = [];
  for (const y of [1.75, 3.55])
    for (const side of [-1, 1]) glass.push(kit.box(0.04, 0.95, 9.4, '#24323e', side * 1.26, y, -0.2, group));
  glass.push(kit.box(2.2, 1.0, 0.04, '#2b3a47', 0, 3.55, 5.21, group));
  kit.box(2.0, 1.1, 0.04, '#2b3a47', 0, 1.75, 5.21, group);
  // the back: the rear windows, the yellow plate, an advert along the side
  kit.box(1.9, 0.8, 0.04, '#24323e', 0, 3.6, -5.21, group);
  kit.box(0.52, 0.12, 0.03, '#f2c230', 0, 0.8, -5.22, group);
  for (const side of [-1, 1]) kit.lit(0.03, 0.55, 4.2, '#f6b83d', side * 1.27, 2.62, -1.4, group, 0.25);
  // the destination blind
  kit.lit(1.8, 0.32, 0.05, '#1a1a1a', 0, 4.2, 5.22, group, 0.4);
  kit.sign(destination, 0, 4.2, 5.26, group, 1.7, '#ffd23f', '#111111');
  lamps(kit, group, 2.5, 10.4, 0.7);
  wheels(
    kit,
    group,
    [
      [-1.1, 3.4],
      [1.1, 3.4],
      [-1.1, -3.2],
      [1.1, -3.2],
    ],
    0.5,
  );
  seated(kit, group, -0.6, 0.95, 4.4, '#2d3a4a', '#5a3825');
  // the best seat in London: upstairs at the front
  return { group, length: 10.4, eye: new T.Vector3(0.45, 3.5, 4.55), glass };
}

// the DLR: three cars, white with a blue band, and a big window at the front where there is no driver
export function dlrTrain(kit: SceneKit) {
  const group = new T.Group();
  for (const dz of [-9.4, 0, 9.4]) {
    kit.box(2.65, 3.1, 9, '#eef1f3', 0, 1.95, dz, group);
    kit.box(2.67, 0.5, 9, '#1c7fc0', 0, 0.75, dz, group);
    for (const side of [-1, 1]) kit.box(0.04, 1.0, 8.2, '#2b3a47', side * 1.33, 2.25, dz, group);
    kit.box(2.0, 0.08, 0.6, '#3b4650', 0, 0.45, dz + 4.5, group);
  }
  // both ends: a blue face round a big window
  const glass: T.Object3D[] = [];
  for (const end of [-1, 1]) {
    const face = kit.box(2.66, 3.12, 0.3, '#1c7fc0', 0, 1.95, end * 13.85, group);
    const window = kit.box(2.2, 1.3, 0.05, '#22303b', 0, 2.3, end * 14.02, group);
    if (end > 0) glass.push(face, window);
  }
  lamps(kit, group, 2.65, 28.2, 1.0);
  return { group, length: 27.8, eye: new T.Vector3(0, 2.15, 12.9), glass };
}

// the Tube: four short cars, red at the front, white sides with a blue band, lit inside
export function tubeTrain(kit: SceneKit) {
  const group = new T.Group();
  for (const dz of [-12.6, -4.2, 4.2, 12.6]) {
    kit.box(2.6, 2.7, 8.2, '#e9ecef', 0, 1.55, dz, group);
    kit.box(2.62, 0.35, 8.2, '#1d3c8f', 0, 0.45, dz, group);
    for (const side of [-1, 1]) kit.lit(0.04, 0.8, 7.2, '#fff3cf', side * 1.31, 1.75, dz, group, 0.5);
  }
  // a red cab at each end
  const glass: T.Object3D[] = [];
  for (const end of [-1, 1]) {
    const cab = kit.box(2.62, 2.72, 0.6, '#c81e1e', 0, 1.55, end * 16.9, group);
    const window = kit.box(1.8, 0.8, 0.05, '#22303b', 0, 1.95, end * 17.22, group);
    if (end > 0) glass.push(cab, window);
  }
  lamps(kit, group, 2.6, 34.4, 0.8);
  return { group, length: 33.6, eye: new T.Vector3(0.5, 1.6, 15.2), glass };
}

// a main-line train (the Elizabeth line's, Thameslink's…) in its line's paint: three long cars, the band along
// the windows, the doors in the line's colour, a sloping nose with its lights, seats and people inside; the
// player's seat at the front of the first car
export function mainlineTrain(kit: SceneKit, paint: { body: string; band: string; doors: string }) {
  const group = new T.Group();
  const car = 16;
  const cars = 3;
  const length = car * cars;
  for (let i = 0; i < cars; i += 1) {
    const z = -length / 2 + car * (i + 0.5);
    kit.box(2.8, 2.9, car - 0.4, paint.body, 0, 1.95, z, group);
    kit.box(2.82, 0.9, car - 0.4, paint.band, 0, 2.3, z, group);
    for (const side of [-1, 1]) {
      // the windows, lit from inside (they glow at night)
      kit.lit(0.04, 0.75, car - 1.6, '#fff1c9', side * 1.42, 2.35, z, group, 0.45);
      for (const dz of [-car / 3, car / 3]) kit.box(0.05, 2.1, 1.3, paint.doors, side * 1.42, 1.65, z + dz, group);
    }
    kit.box(2.6, 0.12, car - 0.6, '#aab1b8', 0, 3.45, z, group);
    kit.lit(1.6, 0.05, car - 1.2, '#fff3cf', 0, 3.3, z, group, 0.6);
    // the seats inside, in pairs either side of the aisle
    for (let k = -car / 2 + 2; k < car / 2 - 1.5; k += 1.8)
      for (const side of [-1, 1]) kit.box(0.95, 0.5, 0.5, '#2f3a6b', side * 0.75, 1.0, z + k, group);
  }
  kit.box(2.6, 0.5, length - 0.4, '#2b2f36', 0, 0.45, 0, group);
  // the front: a sloping nose with the windscreen and the lights
  const glass: T.Object3D[] = [];
  const nose = kit.box(2.8, 1.8, 2.4, paint.band, 0, 1.4, length / 2 + 0.8, group);
  nose.rotation.x = 0.35;
  const screen = kit.box(2.2, 1.0, 0.05, '#1d2731', 0, 2.6, length / 2 + 0.25, group);
  glass.push(nose, screen);
  kit.box(2.8, 1.5, 2, paint.band, 0, 1.3, -length / 2 - 0.6, group);
  lamps(kit, group, 2.8, length + 2.8, 1.0);
  return { group, length: length + 2, eye: new T.Vector3(0.75, 2.0, length / 2 - 5.5), glass };
}

// traffic: a car of some colour, a white van, a black cab or a bus
export function car(kit: SceneKit, colour: string) {
  const group = new T.Group();
  kit.box(1.8, 0.7, 4.3, colour, 0, 0.65, 0, group);
  kit.box(1.6, 0.6, 2.2, colour, 0, 1.25, -0.25, group);
  for (const side of [-1, 1]) kit.box(0.04, 0.42, 1.9, '#33414d', side * 0.81, 1.27, -0.25, group);
  kit.box(1.45, 0.45, 0.04, '#3a4a57', 0, 1.27, 0.86, group);
  lamps(kit, group, 1.8, 4.3, 0.75);
  wheels(
    kit,
    group,
    [
      [-0.82, 1.35],
      [0.82, 1.35],
      [-0.82, -1.35],
      [0.82, -1.35],
    ],
    0.33,
  );
  return { group, length: 4.3 };
}
export function van(kit: SceneKit) {
  const group = new T.Group();
  kit.box(2.0, 2.2, 5.6, '#f2f3f1', 0, 1.45, -0.3, group);
  kit.box(1.9, 0.9, 1.0, '#f2f3f1', 0, 0.95, 2.8, group);
  kit.box(1.8, 0.7, 0.04, '#3a4a57', 0, 1.9, 2.42, group);
  lamps(kit, group, 2.0, 6.2, 0.8);
  wheels(
    kit,
    group,
    [
      [-0.9, 2.0],
      [0.9, 2.0],
      [-0.9, -2.0],
      [0.9, -2.0],
    ],
    0.38,
  );
  return { group, length: 6.2 };
}

// someone on the pavement: legs that swing as they walk, a coat, a head; `step` moves their legs
export function walker(kit: SceneKit, coat: string, legs: string, skin: string) {
  const group = new T.Group();
  const left = new T.Group();
  const right = new T.Group();
  left.position.set(0.11, 0.86, 0);
  right.position.set(-0.11, 0.86, 0);
  kit.box(0.14, 0.86, 0.16, legs, 0, -0.43, 0, left);
  kit.box(0.14, 0.86, 0.16, legs, 0, -0.43, 0, right);
  group.add(left, right);
  kit.box(0.46, 0.66, 0.28, coat, 0, 1.2, 0, group);
  kit.sphere(0.13, skin, 0, 1.68, 0, group);
  return {
    group,
    step: (phase: number) => {
      left.rotation.x = Math.sin(phase) * 0.45;
      right.rotation.x = -Math.sin(phase) * 0.45;
    },
  };
}
